/**

Coded by www.techhighway.ca

*/

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

// @mui material components

import EmailIcon from "@mui/icons-material/Email";
import InsightsIcon from '@mui/icons-material/Insights';
import SupportAgent from '@mui/icons-material/SupportAgent';
import { IconButton, Tooltip } from "@mui/material";
import { purple } from '@mui/material/colors';
import CallType, { Duration, Status } from "components/Icon/Call";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import MDAvatar from "components/MDAvatar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import CallService from "services/call-service";
import { convertSecondsToTime, formatDateTime, formatPhone } from "utility/helpers";

const AudioPlayer = ({ src }) => {
  return (
    <audio src={src} preload="metadata" controls />
  );
};

const AgentAvatar = ({ ...props }) => {
  return (
    <MDAvatar size="sm" shadow="md" {...props}  title="Agent" alt="Agent" sx={{ bgcolor: purple[500] }}>
      <SupportAgent sx={{ color: 'white', fontSize: '1.5rem !important' }} />
    </MDAvatar>
  );
};
const CustomerAvatar = ({ ...props }) => {
  return (
    <MDAvatar src="/images/user-2.jpg" size="sm" shadow="md" {...props} title="Customer" alt="Customer" sx={{ borderColor: purple[500] }}>
      {/* <Person sx={{ color: 'white', fontSize: '1.5rem !important' }} /> */}
    </MDAvatar>
  );
};

function TypingText({ text = '', speed = 100 }) {
  const [displayedText, setDisplayedText] = useState('');

  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      //if (prev) {
        setDisplayedText((prev) => prev + text[i]);
      //}
      i++;
      if (i >= text.length) clearInterval(interval);
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed]);
  return <span>{displayedText}</span>;
}

// @mui icons
import "./styles.css";
function Form() {
  const { action } = useParams();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const navigate = useNavigate();

  const [headerTitle, setHeaderTitle] = useState("");
  const [call, setData] = useState({
    id: "",
    call_type: "",
    started_at: "",
    agent_phone: "",
    customer_phone: "",
    end_reason: "",
    failed: -1,
    duration: 0,
    agent: {
      name: "",
      id: ""
    },
    details: {
      summary: "",
      messages: []
    }
  });
  useEffect(() => {
    if (action === "add" && id == null) return;

    const showCall = async () => {
      try {
        const call = await CallService.showCall(id);
        setData(call);
        setHeaderTitle("Id #" + call.id);
      } catch (res) {
        if (res.hasOwnProperty("message")) {
          const state = { color: "error", icon: "warning", msg: res.message, title: "Call" };
          navigate("/manage-calls", { state });
        } else {
          const state = {
            color: "error",
            icon: "warning",
            msg: res.errors[0].detail,
            title: "Call",
          };
          navigate("/manage-call", { state });
        }
      }
    };
    showCall();
  }, []);

  return (
    <>
      <DashboardLayout
        breadcrumbsTitle={action === "add" ? "Add Call" : `Call Details : ${headerTitle}`}
      >
        <MDBox py={0} px={0} class="chat-application">
          {action === "edit" ? (
            <>
              <MDBox className="app-chat" display="flex">
                {/* Sidebar */}
                <MDBox className="chat-content" display="flex" width="100%">
                  <MDBox className="sidebar" width="300px" borderRight={1} borderColor="divider">
                    <MDBox className="sidebar-content">
                      <MDBox className="sidebar-menu chat-sidebar list-group position-relative">
                        <MDBox className="sidebar-list-padding app-sidebar sidenav">
                          {/* Sidebar Header */}
                          <MDBox className="sidebar-header" display="flex" justifyContent="space-between" alignItems="center" p={2}>
                            <MDBox display="flex" alignItems="center" gap={1}>
                              <AgentAvatar size="md" />
                              <MDBox ml={1}>
                                <MDTypography
                                  color="info"
                                  component={Link}
                                  to={`/manage-agents/edit?id=${call.agent?.id}`}
                                  display="block"
                                  variant="subtitle2"
                                >
                                  {call.agent.name} (#{call.agent.id})
                                </MDTypography>
                                <CallType title="Call Type & Agent Phone" include_type={true} type={call.call_type} value={formatPhone(call.agent_phone)} />
                              </MDBox>
                            </MDBox>
                          </MDBox>
                          <MDBox className="sidebar-search">
                            <AudioPlayer
                              src={call.details.recording}
                            />
                          </MDBox>
                          <MDBox px={2} py={1}>
                            <MDBox display="flex" alignItems="center" mb={0} pb={0}>
                              <InsightsIcon fontSize="medium" color="primary" sx={{ mr: 1 }} />
                              <MDTypography color="textPrimary">
                                Call Summary
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                          {/* Sidebar Chat Content */}
                          <MDBox className="sidebar-content sidebar-chat" sx={{
                            background: 'linear-gradient(to right,rgb(240, 49, 234),rgb(11, 17, 239))',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                          }}>
                            {call.details.summary}
                            {/* <TypingText text={call.details.summary} speed={20} /> */}
                          </MDBox>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </MDBox>

                  {/* Content Area */}
                  <MDBox flex={1} className="chat-content-area">
                    {/* Chat Header */}
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2} borderBottom={1} borderColor="divider">
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <CustomerAvatar size="lg" />
                        <MDBox pb={1}>
                          <MDTypography variant="subtitle2" fontWeight={700}>
                            Customer
                          </MDTypography>
                          <MDBox display="flex" alignItems="center" gap={1} mt={1}>
                            <CallType title="Call Type & Customer Phone" type={call.call_type} value={formatPhone(call.customer_phone)} />
                            <Status title="Call End Reason" value={call.end_reason ? call.end_reason : 'Call In Progress'} />
                            {call.failed > -1 && (
                              <Status title="Success Evaluation?" value={call.failed ? "Fail" : "Pass"} />
                            )}
                          </MDBox>
                        </MDBox>
                      </MDBox>
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <Duration title="Call Time & Duration" value={`${formatDateTime(call.started_at, 'DD MMM, YYYY hh:mm A')} • ${convertSecondsToTime(call.duration)}`} />
                        <IconButton sx={{
                          transition: 'color 0.3s, transform 0.3s',
                          '&:hover': {
                            color: '#1976d2',      // change color on hover
                            transform: 'scale(1.2)' // optional zoom effect
                          }
                        }}>
                          <Tooltip title="Email This Communication">
                            <EmailIcon />
                          </Tooltip>
                        </IconButton>
                      </MDBox>
                    </MDBox>

                    {/* Chat Area */}
                    <MDBox
                      className="chat-area"
                      sx={{
                        backgroundImage: `url('/images/chat-bg.jpg')`,
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center center',
                        flexGrow: 1,
                        p: 2,
                        overflowY: 'auto'
                      }}
                    >
                      <MDBox className="chats">
                        {call?.details?.messages.map(record => {
                          if (record.role === 'bot') { //Chat Left aka Agent
                            return (
                              <MDBox className="chat chat" display="flex" alignItems="flex-start" pb={4} >
                                <AgentAvatar className="chat-avatar" />
                                <MDBox className="chat-body" maxWidth="65%">
                                  <MDBox className="chat-text">
                                    <MDTypography variant="body2">
                                      {record.message}
                                    </MDTypography>
                                  </MDBox>
                                  {/* <small>{formatDateTimeTimestamp(record.message.time, 'DD MMM, hh:mm:ss A')}</small> */}
                                </MDBox>
                              </MDBox>
                            );
                          } else { // Chat Right aka User
                            return (
                              <MDBox className="chat chat-right" display="flex" justifyContent="flex-end" alignItems="flex-end" pb={4}>
                                <CustomerAvatar className="chat-avatar" />
                                <MDBox className="chat-body" maxWidth="65%">
                                  <MDBox className="chat-text">
                                    <MDTypography variant="body2">{record.message}</MDTypography>
                                  </MDBox>
                                </MDBox>
                                {/* <small>{formatDateTimeTimestamp(record.message.time, 'DD MMM, hh:mm:ss A')}</small> */}
                              </MDBox>
                            );
                          }
                        })}
                        
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </MDBox>
              <MDBox mt={1} display="flex">
                <MDButton
                  sx={{ ml: "auto" }}
                  component={Link}
                  to="/manage-calls"
                  variant="gradient"
                  color="primary"
                >
                  Back to Call List
                </MDButton>
              </MDBox>
            </>
          ) : (
            <MDTypography
              display="block"
              variant="body2"
              color="error"
              textAlign="center"  
            >
              You cannot view call details directly.
            </MDTypography>
          )}
        </MDBox>
      </DashboardLayout>
    </>
  );
}

export default Form;
